import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient } from '@wix/sdk-types';
import {
  AbandonedCheckout,
  AbandonedCheckoutCreatedEnvelope,
  AbandonedCheckoutDeletedEnvelope,
  AbandonedCheckoutNonNullableFields,
  AbandonedCheckoutRecoveredEnvelope,
  AbandonedCheckoutUpdatedEnvelope,
  AbandonedCheckoutsQueryBuilder,
  AddAbandonedCheckoutActivityOptions,
  AddAbandonedCheckoutActivityResponse,
  AddAbandonedCheckoutActivityResponseNonNullableFields,
  RawHttpResponse,
  RawHttpResponseNonNullableFields,
  SearchAbandonedCheckoutsOptions,
  SearchAbandonedCheckoutsResponse,
  SearchAbandonedCheckoutsResponseNonNullableFields,
  addAbandonedCheckoutActivity as universalAddAbandonedCheckoutActivity,
  deleteAbandonedCheckout as universalDeleteAbandonedCheckout,
  getAbandonedCheckout as universalGetAbandonedCheckout,
  queryAbandonedCheckouts as universalQueryAbandonedCheckouts,
  redirectToCheckout as universalRedirectToCheckout,
  searchAbandonedCheckouts as universalSearchAbandonedCheckouts,
} from './ecom-v1-abandoned-checkout-abandoned-checkouts.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function getAbandonedCheckout(
  httpClient: HttpClient
): GetAbandonedCheckoutSignature {
  return (abandonedCheckoutId: string) =>
    universalGetAbandonedCheckout(
      abandonedCheckoutId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAbandonedCheckoutSignature {
  /**
   * Retrieves an abandoned checkout.
   * @param - Abandoned checkout ID.
   * @returns The requested abandoned checkout.
   */
  (abandonedCheckoutId: string): Promise<
    AbandonedCheckout & AbandonedCheckoutNonNullableFields
  >;
}

export function deleteAbandonedCheckout(
  httpClient: HttpClient
): DeleteAbandonedCheckoutSignature {
  return (abandonedCheckoutId: string) =>
    universalDeleteAbandonedCheckout(
      abandonedCheckoutId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteAbandonedCheckoutSignature {
  /**
   * Deletes an abandoned checkout.
   * @param - Id of the abandoned checkout to delete
   */
  (abandonedCheckoutId: string): Promise<void>;
}

export function queryAbandonedCheckouts(
  httpClient: HttpClient
): QueryAbandonedCheckoutsSignature {
  return () =>
    universalQueryAbandonedCheckouts(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryAbandonedCheckoutsSignature {
  /**
   * Creates a query to retrieve a list of abandoned checkouts.
   *
   * The `queryAbandonedCheckouts()` function builds a query to retrieve a list of abandoned checkouts and returns a `ResultsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
   *
   * You can refine the query by chaining `ResultsQueryBuilder` functions onto the query. `ResultsQueryBuilder` functions enable you to sort, filter, and control the results `queryAbandonedCheckouts()` returns.
   */
  (): AbandonedCheckoutsQueryBuilder;
}

export function searchAbandonedCheckouts(
  httpClient: HttpClient
): SearchAbandonedCheckoutsSignature {
  return (options?: SearchAbandonedCheckoutsOptions) =>
    universalSearchAbandonedCheckouts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchAbandonedCheckoutsSignature {
  /**
   * Retrieves a list of abandoned checkouts, given the provided paging, filtering, search details and sorting. Up to 100 abandoned checkouts can be returned per request.
   */
  (options?: SearchAbandonedCheckoutsOptions | undefined): Promise<
    SearchAbandonedCheckoutsResponse &
      SearchAbandonedCheckoutsResponseNonNullableFields
  >;
}

/** @internal */
export function addAbandonedCheckoutActivity(
  httpClient: HttpClient
): AddAbandonedCheckoutActivitySignature {
  return (
    abandonedCheckoutId: string | null,
    options?: AddAbandonedCheckoutActivityOptions
  ) =>
    universalAddAbandonedCheckoutActivity(
      abandonedCheckoutId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddAbandonedCheckoutActivitySignature {
  /**
   * Adds a new abandoned checkout activity.
   * @param - Id of the abandoned checkout to update
   */
  (
    abandonedCheckoutId: string | null,
    options?: AddAbandonedCheckoutActivityOptions | undefined
  ): Promise<
    AddAbandonedCheckoutActivityResponse &
      AddAbandonedCheckoutActivityResponseNonNullableFields
  >;
}

export function redirectToCheckout(
  httpClient: HttpClient
): RedirectToCheckoutSignature {
  return (abandonedCheckoutId: string, metasiteId: string) =>
    universalRedirectToCheckout(
      abandonedCheckoutId,
      metasiteId,
      // @ts-ignore
      { httpClient }
    );
}

interface RedirectToCheckoutSignature {
  /**
   * Redirects an abandoned checkout to its checkout page.
   * @param - ID of the abandoned checkout.
   * @param - Metasite ID of the site associated with the abandoned checkout.
   */
  (abandonedCheckoutId: string, metasiteId: string): Promise<
    RawHttpResponse & RawHttpResponseNonNullableFields
  >;
}

export const onAbandonedCheckoutCreated = EventDefinition(
  'wix.ecom.v1.abandoned_checkout_created',
  true,
  (event: AbandonedCheckoutCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.checkoutRecoveredDate' },
            { path: 'entity.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<AbandonedCheckoutCreatedEnvelope>();
export const onAbandonedCheckoutDeleted = EventDefinition(
  'wix.ecom.v1.abandoned_checkout_deleted',
  true,
  (event: AbandonedCheckoutDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.checkoutRecoveredDate' },
            { path: 'undefined.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<AbandonedCheckoutDeletedEnvelope>();
export const onAbandonedCheckoutRecovered = EventDefinition(
  'wix.ecom.v1.abandoned_checkout_recovered',
  true,
  (event: AbandonedCheckoutRecoveredEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.abandonedCheckout.createdDate' },
            { path: 'data.abandonedCheckout.updatedDate' },
            { path: 'data.abandonedCheckout.checkoutRecoveredDate' },
            { path: 'data.abandonedCheckout.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<AbandonedCheckoutRecoveredEnvelope>();
export const onAbandonedCheckoutUpdated = EventDefinition(
  'wix.ecom.v1.abandoned_checkout_updated',
  true,
  (event: AbandonedCheckoutUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.checkoutRecoveredDate' },
            { path: 'entity.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<AbandonedCheckoutUpdatedEnvelope>();

export {
  AbandonedCheckout,
  AbandonedCheckoutCreatedEnvelope,
  AbandonedCheckoutDeletedEnvelope,
  AbandonedCheckoutNonNullableFields,
  AbandonedCheckoutRecovered,
  AbandonedCheckoutRecoveredEnvelope,
  AbandonedCheckoutUpdatedEnvelope,
  AbandonedCheckoutsQueryBuilder,
  AbandonedCheckoutsQueryResult,
  ActionEvent,
  Activity,
  ActivityType,
  AddAbandonedCheckoutActivityOptions,
  AddAbandonedCheckoutActivityRequest,
  AddAbandonedCheckoutActivityResponse,
  AddAbandonedCheckoutActivityResponseNonNullableFields,
  BaseEventMetadata,
  BuyerInfo,
  Cancel,
  CartAbandonedEvent,
  CartRecoveredEvent,
  CommonCursorPaging,
  CommonCursors,
  CommonPaging,
  CommonPagingMetadataV2,
  CommonSortOrder,
  CommonSorting,
  Complete,
  CursorPaging,
  Cursors,
  DeleteAbandonedCheckoutRequest,
  DeleteAbandonedCheckoutResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FullAddressContactDetails,
  GetAbandonedCheckoutRequest,
  GetAbandonedCheckoutResponse,
  GetAbandonedCheckoutResponseNonNullableFields,
  HeadersEntry,
  IdentificationData,
  IdentificationDataIdOneOf,
  Identity,
  MessageEnvelope,
  Mode,
  MultiCurrencyPrice,
  Paging,
  PagingMetadataV2,
  QueryAbandonedCheckoutsRequest,
  QueryAbandonedCheckoutsResponse,
  QueryAbandonedCheckoutsResponseNonNullableFields,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RawHttpResponse,
  RawHttpResponseNonNullableFields,
  RedirectToCheckoutRequest,
  Reschedule,
  RestoreInfo,
  Search,
  SearchAbandonedCheckoutsOptions,
  SearchAbandonedCheckoutsRequest,
  SearchAbandonedCheckoutsResponse,
  SearchAbandonedCheckoutsResponseNonNullableFields,
  SearchDetails,
  SearchPagingMethodOneOf,
  SortOrder,
  Sorting,
  Status,
  Task,
  TaskAction,
  TaskActionActionOneOf,
  TaskKey,
  Totals,
  V1BuyerInfo,
  V1BuyerInfoIdOneOf,
  VatId,
  VatType,
  WebhookIdentityType,
} from './ecom-v1-abandoned-checkout-abandoned-checkouts.universal.js';
